import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, Link, useParams } from 'react-router'

import { Flex, Image } from 'antd'

import dayjs from 'dayjs'

import { HTML } from '../../components/markup/HTML'
import { Page } from '../../components/page'
import { useArticleQuery } from '../../graphql'
import routes from '../../routes'
import NotFound from '../NotFound'

import { AuthorMeta } from './ArticleCard'
import { Comments } from './Comments'
import { Context } from './Context'
import { RelatedArticles } from './RelatedArticles'
import { TableOfContent } from './TableOfContent'

const ArticlePage: FC = () => {
  const { slug = '' } = useParams<{ slug: string }>()
  const { data, loading } = useArticleQuery({ variables: { documentId: slug, slug } })
  const article = data?.article
  const { formatMessage } = useIntl()
  if (!article && !loading) return <NotFound />

  return (
    <Page
      loading={loading}
      title={article?.title}
      extra={
        <AuthorMeta
          avatar={article?.author?.avatar?.url}
          title={article?.author?.name}
          description={dayjs(article?.publishedAt).format('DD MMMM, HH:mm')}
        />
      }
      breadcrumbs={[
        {
          title: <Link to={generatePath(routes.blog)}>{formatMessage({ id: 'blog.header' })}</Link>,
        },
      ]}
      seo={article?.seo}
    >
      <Flex style={{ position: 'fixed', left: 0, top: 120, width: 'calc(50% - 600px)' }} vertical gap={8}>
        <TableOfContent />
      </Flex>
      <Flex vertical gap={24}>
        <Image
          alt={article?.cover?.alternativeText ?? article?.title}
          preview={false}
          width={'100%'}
          height={480}
          style={{ objectFit: 'cover', objectPosition: 'top' }}
          src={article?.cover?.url}
        />
        <HTML>{article?.description}</HTML>
        <RelatedArticles data={article?.tags?.flatMap((it) => it?.articles ?? []) ?? []} />
        <Comments articleId={article?.documentId} />
      </Flex>
    </Page>
  )
}

const Article: FC = () => {
  const [heading, setHeading] = useState('')

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll<HTMLHeadingElement>('h2,h3')
      const scrollPosition = window.scrollY

      sections.forEach((section) => {
        const sectionTop = section.offsetTop - 100
        const sectionBottom = sectionTop + section.offsetHeight

        if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
          setHeading(section.id)
        }
      })
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <Context.Provider value={{ heading }}>
      <ArticlePage />
    </Context.Provider>
  )
}

export { Article as default }
